import { Check, InfoOutlined, KeyboardArrowLeft } from "@mui/icons-material";
import { Backdrop, Button, Dialog, Tooltip } from "@mui/material";
import cx from "classnames";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ReactComponent as XMark } from "assets/x-mark.svg";
import DotElastic from "components/DotElastic";
import { PLANS } from "constants/constants";
import { Plan } from "models/Payment";
import { Subscription } from "models/User";
import {
  CANCEL_PLAN_INDEX,
  MY_PLAN_INDEX,
  SELECT_PAYMENT_METHOD_INDEX,
  VERIFY_EMAIL_INDEX,
} from "pages/UpgradePlanFlow/UpgradePlanFlow";
import {
  downgradeSubscriptionPlan,
  getPlans,
} from "services/SubscriptionService";
import {
  formatDate,
  formatPrice,
  formatSize,
  isEmailVerified,
} from "utils/helper";

import styles from "./Plans.module.scss";

export const FeatureTip = ({ text }: { text: string }) => {
  return (
    <Tooltip
      arrow
      slotProps={{
        tooltip: {
          sx: {
            padding: "0px",
            backgroundColor: "unset",
            fontFamily: "Inter",
          },
        },
        arrow: {
          sx: {
            color: "#22242A",
          },
        },
      }}
      placement="top"
      title={<div className={styles.tooltip}>{text}</div>}
    >
      <InfoOutlined className={styles.infoIcon} />
    </Tooltip>
  );
};

interface PlansProps {
  setSelectedPlan: React.Dispatch<React.SetStateAction<Plan | null>>;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  onBack?: () => void;
  subscription: Subscription;
}

const Plans: React.FC<PlansProps> = ({
  setSelectedPlan,
  setStep,
  onBack,
  subscription,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [downgradePlan, setDowngradePlan] = useState<Plan | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const stepAfterPlanSelection = isEmailVerified()
    ? SELECT_PAYMENT_METHOD_INDEX
    : VERIFY_EMAIL_INDEX;

  const maskFrameCost = useMemo(() => {
    const price =
      subscription.plan.extra_frame_cost.unit_amount_decimal > 0
        ? subscription.plan.extra_frame_cost
        : plans.find((plan) => plan.extra_frame_cost.unit_amount_decimal > 0)
            ?.extra_frame_cost;
    return price ? formatPrice(price, 3) : null;
  }, [plans, subscription.plan.extra_frame_cost]);

  const onDowngrade = useCallback(() => {
    if (downgradePlan) {
      setIsLoading(true);
      downgradeSubscriptionPlan(subscription.id, downgradePlan.id)
        .then(() => {
          setStep(MY_PLAN_INDEX);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [downgradePlan, setStep, subscription.id]);

  useEffect(() => {
    getPlans(subscription.id).then((data) => {
      setPlans(data);
    });
  }, [subscription.id]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const planId = searchParams.get("plan-id");
    if (planId) {
      const plan = plans.find((value) => value.id === +planId);
      if (plan) {
        if (
          plan.price.unit_amount_decimal >
          subscription.plan.price.unit_amount_decimal
        ) {
          setSelectedPlan(plan);
          setStep(stepAfterPlanSelection);
        }
        searchParams.delete("plan-id");
        navigate(
          {
            pathname: location.pathname,
            search: searchParams.toString(),
          },
          { replace: true },
        );
      }
    }
  }, [
    location.pathname,
    location.search,
    navigate,
    plans,
    setSelectedPlan,
    setStep,
    stepAfterPlanSelection,
    subscription.plan.price.unit_amount_decimal,
  ]);

  return (
    <div className={styles.container}>
      {onBack && (
        <div className={styles.back} onClick={onBack}>
          <KeyboardArrowLeft className={styles.arrowLeft} />
          <span>Back</span>
        </div>
      )}

      <div className={styles.header}>
        <div className={styles.heading}>
          Join 10,000+ creators transforming VFX with Slapshot
        </div>
      </div>
      {plans.length ? (
        <>
          <div className={styles.body}>
            <div className={styles.scrollableTable}>
              <table border={1} className={styles.table}>
                <thead>
                  <tr>
                    <td aria-label="Name" style={{ background: "#13131a" }} />
                    {plans.map((plan, index) => (
                      <td
                        key={plan.id}
                        style={{
                          backgroundColor:
                            subscription.plan.id === plan.id
                              ? "#484899"
                              : "#13131a",
                        }}
                      >
                        <div className={styles.nameContainer}>
                          {index === 2 && (
                            <div className={styles.popularCon}>
                              <div className={styles.popularText}>POPULAR</div>
                            </div>
                          )}
                          <p className={styles.planName}>{plan.name}</p>
                          <p className={styles.planDesc}>{plan.description}</p>
                          <p className={styles.planAmount}>
                            {plan.price.unit_amount_decimal
                              ? formatPrice(plan.price)
                              : formatPrice({
                                  currency:
                                    plans.at(-1)?.price.currency || "usd",
                                  unit_amount_decimal: 0,
                                })}
                            <span>/ month</span>
                          </p>
                        </div>
                      </td>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td>
                      <div className={styles.tableHeader}>
                        Unlimited Seats
                        <FeatureTip text="Invite any number of collaborators to your workspace" />
                      </div>
                    </td>
                    {plans.map((plan) => (
                      <td
                        key={plan.id}
                        style={{
                          backgroundColor:
                            subscription.plan.id === plan.id
                              ? "#232342"
                              : "#13131a",
                        }}
                      >
                        <div className={styles.checkIconCon}>
                          <Check />
                        </div>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td>
                      <div className={styles.tableHeader}>
                        Storage
                        <FeatureTip text="Maximum storage space available" />
                      </div>
                    </td>
                    {plans.map((plan) => (
                      <td
                        key={plan.id}
                        style={{
                          backgroundColor:
                            subscription.plan.id === plan.id
                              ? "#232342"
                              : "#13131a",
                        }}
                      >
                        {formatSize(plan.storage, "GB")}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td>
                      <div className={styles.tableHeader}>
                        Resolution Limit
                        <FeatureTip text="Maximum resolution allowed for export" />
                      </div>
                    </td>
                    {plans.map((plan) => (
                      <td
                        key={plan.id}
                        style={{
                          backgroundColor:
                            subscription.plan.id === plan.id
                              ? "#232342"
                              : "#13131a",
                        }}
                      >
                        <div>{`(${plan.resolution.name}) ${plan.resolution.dimension[0]} x ${plan.resolution.dimension[1]}`}</div>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td>
                      <div className={styles.tableHeader}>
                        Export Frames included
                        <FeatureTip text="The number of frames you can export without incurring additional charges" />
                      </div>
                    </td>
                    {plans.map((plan) => {
                      return (
                        <td
                          key={plan.id}
                          style={{
                            backgroundColor:
                              subscription.plan.id === plan.id
                                ? "#232342"
                                : "#13131a",
                          }}
                        >
                          <div>{plan.frames}</div>
                          <div className={styles.extraFrameCost}>
                            {plan.extra_frame_cost.unit_amount_decimal > 0
                              ? `${formatPrice(plan.extra_frame_cost, 3)}/frame once over`
                              : "--"}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td>
                      <div className={styles.tableHeader}>
                        Comfy or API Access
                        <span className={styles.addOnBadge}>ADD ON</span>
                        <FeatureTip text="Comfy integration usage or direct API usage. Billed weekly based on frames you process." />
                      </div>
                      <div className={styles.featureSubtitle}>
                        Invoiced weekly on usage
                      </div>
                    </td>
                    {plans.map((plan) => (
                      <td
                        key={plan.id}
                        style={{
                          backgroundColor:
                            subscription.plan.id === plan.id
                              ? "#232342"
                              : "#13131a",
                        }}
                      >
                        <div className={styles.apiFrameCost}>
                          {formatPrice(plan.autopilot_api_frame_cost)}
                          <span>/frame</span>
                        </div>
                        <div className={styles.featureSubtitle}>
                          Once over{" "}
                          {plan.autopilot_api_free_frames.toLocaleString()}{" "}
                          frames
                        </div>
                        {maskFrameCost && (
                          <div className={styles.featureSubtitle}>
                            <span className={styles.highlightPrice}>
                              {maskFrameCost}
                            </span>
                            /frame per layer when using an input mask with roto
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                  {/* <tr>
                    <td>
                      <div
                        className={styles.tableHeader}
                        style={{ color: "white", fontWeight: "700" }}
                      >
                        Usage Limits
                      </div>
                    </td>
                    {plans.map((plan) => {
                      return (
                        <td
                          key={plan.id}
                          style={{
                            backgroundColor:
                              subscription.plan.id === plan.id
                                ? "#232342"
                                : "#13131a",
                          }}
                        />
                      );
                    })}
                  </tr>
                  <tr>
                    <td>
                      <div className={styles.tableHeader}>Predictions</div>
                    </td>
                    {plans.map((plan) => {
                      return (
                        <td
                          key={plan.id}
                          style={{
                            backgroundColor:
                              subscription.plan.id === plan.id
                                ? "#232342"
                                : "#13131a",
                          }}
                        >
                          <div>
                            {plan.inference_throttling
                              ? `${plan.inference_throttling} per hour`
                              : "Unlimited"}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td>
                      <div className={styles.tableHeader}>Exports</div>
                    </td>
                    {plans.map((plan) => {
                      return (
                        <td
                          key={plan.id}
                          style={{
                            backgroundColor:
                              subscription.plan.id === plan.id
                                ? "#232342"
                                : "#13131a",
                          }}
                        >
                          <div>
                            {plan.export_throttling
                              ? `${plan.export_throttling} per hour`
                              : "Unlimited"}
                          </div>
                        </td>
                      );
                    })}
                  </tr> */}
                </tbody>
                <tfoot>
                  <tr>
                    <td
                      aria-label="Get Plan"
                      style={{ background: "#13131a" }}
                    />
                    {plans.map((plan) => (
                      <td
                        key={plan.id}
                        style={{
                          backgroundColor:
                            subscription.plan.id === plan.id
                              ? "#1B1B2F"
                              : "#13131a",
                        }}
                      >
                        {plan.id !== subscription?.plan.id ||
                        !subscription.renewal_date ? (
                          <Button
                            className={cx(
                              styles.slapShotButton,
                              styles.getPlanBtn,
                            )}
                            disabled={
                              subscription?.is_processing_payment ||
                              (plan.name === PLANS.FREE &&
                                !!subscription.renewal_date &&
                                subscription.renewal_plan.name === PLANS.FREE)
                            }
                            onClick={() => {
                              if (plan.name !== PLANS.FREE) {
                                if (
                                  subscription &&
                                  subscription.renewal_date &&
                                  plan.price.unit_amount_decimal <=
                                    subscription?.plan.price.unit_amount_decimal
                                ) {
                                  setDowngradePlan(plan);
                                } else {
                                  setSelectedPlan(plan);
                                  setStep(stepAfterPlanSelection);
                                }
                              } else if (
                                !subscription.renewal_date ||
                                subscription.renewal_plan.name !== PLANS.FREE
                              ) {
                                setStep(CANCEL_PLAN_INDEX);
                              }
                            }}
                          >
                            Get {plan.name}
                          </Button>
                        ) : (
                          <div className={styles.currentPlanText}>
                            Current Plan
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          <div className={styles.footer}>
            Contact us for the{" "}
            <a href="https://support.slapshot.ai/en/support">Enterprise Plan</a>
          </div>
        </>
      ) : (
        <div className={styles.loading}>
          <DotElastic />
        </div>
      )}

      <Dialog
        open={!!downgradePlan && !isLoading}
        slotProps={{
          paper: {
            className: "dialog",
          },
        }}
      >
        {downgradePlan && (
          <div className="modalContainer">
            <div className="modalHeader">
              <h3 className="modalTitle">Confirm Downgrade</h3>
              <button
                onClick={() => {
                  setDowngradePlan(null);
                }}
                type="button"
                className="closeButton"
              >
                <XMark />
              </button>
            </div>
            {subscription && (
              <div className={styles.confirmationText}>
                When your billing period ends on{" "}
                {subscription.renewal_date
                  ? formatDate(subscription?.renewal_date)
                  : ""}
                , you will be downgraded to the {downgradePlan.name} plan and{" "}
                <strong>
                  may lose access to some premium features and asset storage
                </strong>
                .
                <br />
                <br /> Please ensure your asset storage usage is below the
                storage limit of the {downgradePlan.name} plan.{" "}
                <strong>
                  Any assets exceeding this limit will be deleted after 90 days
                </strong>
                .
                <br />
                <br /> If you would like to keep your assets, contact{" "}
                <strong>support@slapshot.ai</strong> to pause your subscription
                instead.
                <br />
                <br /> Are you sure you want to downgrade your subscription?
              </div>
            )}
            <div className="modalActions">
              <Button
                type="button"
                className={styles.noBackgroundButton}
                onClick={() => {
                  setDowngradePlan(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className={styles.outlineButtonRed}
                onClick={onDowngrade}
              >
                Downgrade
              </Button>
            </div>
          </div>
        )}
      </Dialog>

      <Backdrop open={isLoading} style={{ zIndex: 10 }}>
        <img src="/loading.gif" alt="" />
      </Backdrop>
    </div>
  );
};
export default Plans;
